import { IWebSocketReceiver } from "@/types/interfaces";
import { BigBashEvent, Game, ScreenContent, WebSocketData } from "@/types/types";
import { sharedStyles } from "@lib/sharedStyles";
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import DisplayContentSwitch from "./DisplayContentSwitch";
import RoomWebsocketController from "./RoomWebsocketController";

@customElement("room-page")
export class RoomPage extends LitElement {
	static readonly styles = [
		sharedStyles,
		css`
			.container {
				border: var(--border-default);
				border-radius: var(--border-radius);
				background: white;
				box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
				padding: 24px;
				display: flex;
				flex-direction: column;
				gap: 16px;
				align-items: center;
				justify-content: center;
			}

			.container-middle {
				display: flex;
				justify-content: space-between;
				align-items: center;
			}

			.container-bottom {
				display: flex;
				flex-direction: column;
				gap: 16px;
				background: var(--color-pink);
			}

			.container > * {
				width: 100%;
			}

			.content-section {
				width: 100%;
				display: flex;
				flex-direction: column;
				align-items: center;
				gap: 16px;
			}
		`
	];

	@state() private isLoading = true;
	@property({}) roomId: string;
	@state() private screenContent: ScreenContent = 'default';
	@state() private event: BigBashEvent | null = null;
	@state() private game: Game | null = null;
	@state() private quiz: any | null = null;
	@state() private popupQuestion: any | null = null;
	@state() private showAnswer: boolean = false;
	@state() private selectedOptionId: string | undefined;

	private webSocketHandler: IWebSocketReceiver;
	private displayContentCreator: DisplayContentSwitch;

	constructor(roomId: string, webSocketHandler: IWebSocketReceiver) {
		super();
		this.roomId = roomId;
		this.webSocketHandler = webSocketHandler;
	}

	connectedCallback() {
		super.connectedCallback();
		this._updateTitle();
		if (!this.webSocketHandler) {
			this.webSocketHandler = new RoomWebsocketController(this.roomId);
		}
		this.displayContentCreator = new DisplayContentSwitch(this.roomId);
		this.webSocketHandler.handleWebSocketConnection(this._handleOnMessage.bind(this));
		this.isLoading = false;

	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.webSocketHandler?.close();
	}

	protected updated(changedProps: Map<string, unknown>) {
		if (changedProps.has("roomId")) {
			this._updateTitle();
		}
	}

	private _updateTitle() {
		if (this.roomId) {
			const name =
				this.roomId.charAt(0).toUpperCase() + this.roomId.slice(1);
			document.title = `Room ${name}`;
		} else {
			document.title = "The Big Bash";
		}
	}

	private _handleOnMessage(websocketData: WebSocketData) {
		if (websocketData.screenContent) {
			this.screenContent = websocketData.screenContent;
			this.event = websocketData.event || null;
			this.game = websocketData.game || null;
			// custom payloads for jeopardy
			// Preserve existing quiz if not explicitly provided in the message
			const anyData = websocketData as any;
			if (Object.prototype.hasOwnProperty.call(anyData, 'quiz')) {
				this.quiz = anyData.quiz ?? null;
			}
			// Update popupQuestion only if explicitly provided; clear when grid starts
			if (Object.prototype.hasOwnProperty.call(anyData, 'question')) {
				this.popupQuestion = anyData.question ?? null;
				this.popupQuestion = anyData.question ?? null;
				this.showAnswer = false; // Reset answer visibility for new question
				this.selectedOptionId = undefined;
			} else if (websocketData.screenContent === 'jeopardy-grid') {
				this.popupQuestion = null;
				this.showAnswer = false;
			}

			if (Object.prototype.hasOwnProperty.call(anyData, 'showAnswer')) {
				this.showAnswer = !!anyData.showAnswer;
			}

			if (Object.prototype.hasOwnProperty.call(anyData, 'selectedOptionId')) {
				this.selectedOptionId = anyData.selectedOptionId;
			}

			this.requestUpdate();
		}
	}

	render() {
		if (this.isLoading) {
			return html`
                <div class="container">
                    <div style="text-align: center; padding: 60px;">
                        Loading...
                    </div>
                </div>
            `;
		}

		return this.displayContentCreator.getDisplayContent(this.screenContent, this.event, this.game, this.quiz, this.popupQuestion, this.showAnswer, this.selectedOptionId);
	}
}

export default RoomPage;